import React, {useCallback, useMemo} from 'react';
import _ from 'underscore';

import * as AuditActions from 'audit/AuditActions';
import {EXPORT_AUDIT_LOG_DISPLAY_STRING} from 'audit/AuditConstants';
import BenchlingConfig from 'BenchlingConfig';
import * as BenchlingUtil from 'BenchlingUtil';
import ExcludeInaccessible from 'graphql-client/ExcludeInaccessible';
import SettingsIconDropdown from 'nav/sidepanel/SettingsIconDropdown';
import ManageTemplateCollectionModal from 'notebook/entry-templates/collections/ManageTemplateCollectionModal';
import {getTemplateCollectionURL} from 'notebook/entry-templates/collections/TemplateCollectionActions';
import legacyRunModal from 'ui-library/legacyRunModal';
import licenses from 'user/Licenses';
import copyToClipboard from 'util/clipboard/copyToClipboard';

import {TemplateCollectionQuery} from './graphql/__generated/TemplateCollectionQuery';
import {
  COPY_TEMPLATE_COLLECTION_LINK_MENU_ITEM_LABEL,
  TEMPLATE_COLLECTION_SETTINGS_MENU_ITEM_LABEL,
} from './TemplateCollectionTypes';

interface TemplateCollectionSettingsIconProps {
  templateCollection: ExcludeInaccessible<TemplateCollectionQuery['templateCollection']>;
}

export default function TemplateCollectionSettingsIcon(
  props: TemplateCollectionSettingsIconProps
): JSX.Element {
  const {templateCollection} = props;

  const handleOpenSettings = useCallback(() => {
    legacyRunModal(ManageTemplateCollectionModal, {
      navigateOnSuccess: !BenchlingConfig.ENABLE_TEMPLATE_COLLECTIONS_IN_WORKSPACE,
      templateCollection,
    });
  }, [templateCollection]);

  const handleExportAuditLogs = useCallback(() => {
    AuditActions.exportAuditLogs([templateCollection.id]);
  }, [templateCollection.id]);

  const handleCopyLink = useCallback(() => {
    const collectionURL = BenchlingUtil.pathToURL(getTemplateCollectionURL(templateCollection));
    copyToClipboard(collectionURL, {showToastOnSuccess: true});
  }, [templateCollection]);

  const options = useMemo(
    () =>
      _.compact([
        {
          name: TEMPLATE_COLLECTION_SETTINGS_MENU_ITEM_LABEL,
          onSelect: handleOpenSettings,
          dataTestAttrs: {component: 'TemplateCollectionSettingsIcon', element: 'settingsOption'},
        },
        licenses.hasCommercialLicense() && {
          name: EXPORT_AUDIT_LOG_DISPLAY_STRING,
          onSelect: handleExportAuditLogs,
          dataTestAttrs: {component: 'TemplateCollectionSettingsIcon', element: 'exportAuditLogOption'},
        },
        {
          name: COPY_TEMPLATE_COLLECTION_LINK_MENU_ITEM_LABEL,
          onSelect: handleCopyLink,
          dataTestAttrs: {component: 'TemplateCollectionSettingsIcon', element: 'copyLinkOption'},
        },
      ]),
    [handleCopyLink, handleExportAuditLogs, handleOpenSettings]
  );

  return <SettingsIconDropdown options={options} />;
}
